import SideModal from "components/SideModal";
import { useTranslation } from "contexts/i18nContext";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useRef, useState } from "react";
import { editSummon } from "services/prosecutorDecipilineService";
import { Complaints, Investigation, Summon } from "types";
import { LoadingIcon } from "utils/constant";
import { SUMMON_STATUS } from "utils/constantValues";
import "../styles/AddVictimModal.css";

interface ModalProps {
  isOpen: boolean;
  summonData: Summon;
  setIsOpen: (isOpen: boolean) => void;
  investigationData: Investigation;
  complaintData: Complaints;
  onSummonRefresh: any;
}

export default function AddSummonModal({
  isOpen,
  setIsOpen,
  summonData,
  investigationData,
  complaintData,
  onSummonRefresh,
}: ModalProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formState, setFormState] = useState<Summon>({
    hearing_date: "",
    region: "",
    zone: "",
    woreda: "",
    kebele: "",
    summary: "",
    files: [] as File[],
    investigation: null,
  });

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormState((prev) => ({
        ...prev,
        files: [...Array.from(e.target.files as FileList)],
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    if (!investigationData?.uu_id) {
      showToast(t("investigationNotFound"), "error");
      return;
    }

    const data = new FormData();
    data.append("hearing_date", formState?.hearing_date);
    data.append("region", formState?.region);
    data.append("zone", formState?.zone);
    data.append("woreda", formState?.woreda);
    data.append("kebele", formState?.kebele);
    data.append("summary", formState?.summary);
    data.append("summon_file", formState?.files[0] || "");
    data.append("investigation", investigationData?.uu_id);
    data.append("status", SUMMON_STATUS.SUMMON_ISSUED);
    data.append(
      "accused_prosecutor_name",
      `${complaintData?.accused_prosecutor_name || ""}`,
    );
    data.append(
      "user_id_accused_prosecutor_id",
      complaintData?.user_id_accused_prosecutor_name || "",
    );
    data.append(
      "assigned_prosecutor",
      complaintData?.assigned_prosecutor || "",
    );
    data.append(
      "user_id_assigned_prosecutor",
      complaintData?.user_id_assigned_prosecutor || "",
    );
    data.append("admin_unit_id", complaintData?.admin_unit_id || "");
    data.append("org_id", complaintData?.org_id || "");

    const isFormData = true;
    const summon_id = summonData?.id || "";

    try {
      // await addSummon(data, isFormData);
      await editSummon(data, summon_id, isFormData);

      onSummonRefresh();
      setIsOpen(false);
      showToast(t("successfullyCreated"), "success");
      setFormState({
        hearing_date: "",
        region: "",
        zone: "",
        woreda: "",
        kebele: "",
        summary: "",
        files: [] as File[],
        investigation: null,
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };
  if (!isOpen) return null;

  return (
    <SideModal isOpen={isOpen} setIsOpen={setIsOpen} title={t("createASummon")}>
      <form onSubmit={handleSubmit} className="victim-form">
        <section className="victim-section">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("hearingDate")}
            </label>
            <input
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              type={"date"}
              name="hearing_date"
              placeholder={t("hearingDate")}
              value={formState.hearing_date}
              onChange={handleChange}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("region")}
              </label>
              <input
                required
                className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                type="text"
                name="region"
                value={formState.region}
                onChange={handleChange}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("zone")}
              </label>
              <input
                required
                className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                type="text"
                name="zone"
                value={formState.zone}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("woreda")}
              </label>
              <input
                required
                className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                type="text"
                name="woreda"
                value={formState.woreda}
                onChange={handleChange}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("kebele")}
              </label>
              <input
                required
                className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                type="text"
                name="kebele"
                value={formState.kebele}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("summary")}
            </label>
            <textarea
              required
              rows={3}
              name="summary"
              value={formState.summary}
              onChange={handleChange}
              placeholder={t("detailsHere")}
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
            />
          </div>
        </section>

        <section className="evidence-section">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("summonFile")}
          </label>
          <div className="file-upload-area" onClick={triggerFileInput}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              // multiple
              style={{ display: "none" }}
            />
            <p className="upload-text">{t("uploadFiles")}</p>
            {formState?.files && formState?.files?.length > 0 && (
              <div className="file-list">
                {formState?.files?.map((file, index) => (
                  <div key={index} className="file-item">
                    {file.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              {t("submitting")}
            </div>
          ) : (
            t("addSummon")
          )}
        </button>
      </form>
    </SideModal>
  );
}
